import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../config/api";
import "./Wishlist.css";

export default function Wishlist() {
  const { user, isLoggedIn, authLoading } = useAuth();

  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [wishlistError, setWishlistError] = useState("");
  const [removingSlug, setRemovingSlug] = useState("");

  useEffect(() => {
    document.title = "My Wishlist | MayaChitro";
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchWishlist() {
      if (!isLoggedIn) {
        setWishlistLoading(false);
        return;
      }

      try {
        setWishlistLoading(true);
        setWishlistError("");

        const response = await fetch(buildApiUrl("/wishlist"), {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        const data = await response.json();

        if (!ignore && response.ok && data.success) {
          setWishlistItems(data.items || data.products || []);
        } else if (!ignore) {
          setWishlistItems([]);
          setWishlistError(data.message || "Could not load wishlist.");
        }
      } catch (error) {
        console.error("Wishlist fetch error:", error);
        if (!ignore) {
          setWishlistItems([]);
          setWishlistError("Could not load wishlist. Please try again.");
        }
      } finally {
        if (!ignore) setWishlistLoading(false);
      }
    }

    fetchWishlist();

    return () => {
      ignore = true;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const handleWishlistUpdated = (event) => {
      const productSlug = event?.detail?.productSlug;
      const saved = event?.detail?.saved;

      if (productSlug && saved === false) {
        setWishlistItems((currentItems) =>
          currentItems.filter((item) => {
            const itemSlug =
              item.slug || item.product_slug || item.product?.slug || item.productSlug;

            return itemSlug !== productSlug;
          })
        );
      }
    };

    window.addEventListener("mayachitro:wishlist-updated", handleWishlistUpdated);

    return () => {
      window.removeEventListener("mayachitro:wishlist-updated", handleWishlistUpdated);
    };
  }, []);

  const normalizedItems = useMemo(() => {
    return wishlistItems
      .map((item) => {
        if (item.product) return item.product;

        return {
          id: item.id || item.product_id || item.productId || item.product_slug,
          productId: item.product_id || item.productId,
          slug: item.slug || item.product_slug,
          title: item.title || item.product_title,
          shortTitle: item.shortTitle || item.short_title || item.product_title,
          coverImage:
            item.coverImage ||
            item.cover_image ||
            item.thumbnail ||
            item.thumbnail_image ||
            item.image,
          thumbnailImage:
            item.thumbnailImage ||
            item.thumbnail_image ||
            item.thumbnail ||
            item.cover_image ||
            item.image,
          personalPrice:
            item.personalPrice || item.personal_price || item.price || item.startingPrice,
          brandSlug: item.brandSlug || item.brand_slug,
          category: item.category || item.category_name,
          badge: item.badge || "",
        };
      })
      .filter((product) => product.slug || product.title);
  }, [wishlistItems]);

  const handleRemove = async (product) => {
    const productSlug = product.slug || product.productSlug;

    if (!productSlug) {
      setWishlistError("Could not remove this item because product slug is missing.");
      return;
    }

    try {
      setRemovingSlug(productSlug);
      setWishlistError("");

      const response = await fetch(buildApiUrl(`/wishlist/${encodeURIComponent(productSlug)}`), {
        method: "DELETE",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setWishlistError(data.message || "Could not remove item from wishlist.");
        return;
      }

      setWishlistItems((currentItems) =>
        currentItems.filter((item) => {
          const itemSlug =
            item.slug || item.product_slug || item.product?.slug || item.productSlug;

          return itemSlug !== productSlug;
        })
      );

      window.dispatchEvent(
        new CustomEvent("mayachitro:wishlist-updated", {
          detail: {
            productSlug,
            saved: false,
          },
        })
      );
    } catch (error) {
      console.error("Wishlist remove error:", error);
      setWishlistError("Could not remove item. Please try again.");
    } finally {
      setRemovingSlug("");
    }
  };

  if (authLoading) {
    return (
      <main className="wishlist-page">
        <section className="wishlist-loader">
          <p className="wishlist-eyebrow">MayaChitro Wishlist</p>
          <h1>Checking your account...</h1>
          <p>Please wait a moment.</p>
        </section>
      </main>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login?redirect=/wishlist" replace />;
  }

  return (
    <main className="wishlist-page">
      <section className="wishlist-topbar">
        <div>
          <p className="wishlist-eyebrow">Saved Visuals</p>
          <h1>My Wishlist</h1>
          <p>
            Review your saved MayaChitro photos and wall art before buying,
            downloading, or licensing.
          </p>
        </div>

        <div className="wishlist-actions">
          <Link to="/shop" className="wishlist-btn wishlist-btn-gold">
            Continue Shopping
          </Link>
          <Link to="/collections" className="wishlist-btn wishlist-btn-outline">
            Collections
          </Link>
        </div>
      </section>

      <section className="wishlist-mini-summary">
        <div>
          <span>Total Saved</span>
          <strong>{normalizedItems.length}</strong>
        </div>

        <div>
          <span>Account</span>
          <strong>{user?.fullName || "Customer"}</strong>
        </div>

        <div>
          <span>Next Step</span>
          <strong>Open product → choose license or download</strong>
        </div>
      </section>

      {wishlistError && (
        <section className="wishlist-notice">
          <strong>Note:</strong> {wishlistError}
        </section>
      )}

      <section className="wishlist-products-section">
        <div className="wishlist-section-heading">
          <div>
            <p className="wishlist-eyebrow">Saved Products</p>
            <h2>Your favorite visuals</h2>
          </div>

          <span>
            {normalizedItems.length} {normalizedItems.length === 1 ? "item" : "items"}
          </span>
        </div>

        {wishlistLoading ? (
          <div className="wishlist-loader compact">
            <h3>Loading wishlist...</h3>
            <p>Your saved items will appear here.</p>
          </div>
        ) : normalizedItems.length > 0 ? (
          <div className="wishlist-grid">
            {normalizedItems.map((product) => (
              <div className="wishlist-product-item" key={product.slug || product.id}>
                <ProductCard product={product} compact />

                <div className="wishlist-card-actions">
                  <Link to={`/product/${product.slug}`} className="wishlist-view-link">
                    View product
                  </Link>

                  <button
                    type="button"
                    className="wishlist-remove-btn"
                    onClick={() => handleRemove(product)}
                    disabled={removingSlug === product.slug}
                  >
                    {removingSlug === product.slug ? "Removing..." : "Remove"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="wishlist-empty-state">
            <p className="wishlist-eyebrow">No Saved Items Yet</p>
            <h2>Start building your visual collection.</h2>
            <p>
              Save photography downloads, printable wall art, and licensing-ready
              visuals so you can compare and buy later.
            </p>

            <div className="wishlist-empty-actions">
              <Link to="/shop?brand=mayachitro" className="wishlist-btn wishlist-btn-gold">
                Shop Photography
              </Link>

            </div>
          </div>
        )}
      </section>
    </main>
  );
}
